// @/components/auth/TokenStatusCard.jsx
// Unified card for completed and invalid token states

import React from "react";
import * as motion from "motion/react-client";
import { INTRO, spring } from '@/components/reusable/animations';
import { Button, ButtonGroup } from "@/components/reusable/button";
import { useContentInvitation } from '@/hooks/content/useContentInvitation';
import WelcomeCard from "./WelcomeCard";
import { HELP_URL } from '@/config/externalLinks';

/**
 * TokenStatusCard — displays token status (completed or invalid) with action buttons
 *
 * @param {"completed"|"invalid"} variant
 * @param {string} [errorMessage] — custom error message for invalid variant
 */
const TokenStatusCard = ({
  variant = "completed",
  errorMessage = "The invitation token is invalid or has expired.",
}) => {
  const content = useContentInvitation();
  const isInvalid = variant === "invalid";

  const title = isInvalid
    ? (content?.tokenInvalidTitle || "Invalid Invitation")
    : (content?.accountRegisteredTitle || "Account Already Registered");

  const description = isInvalid
    ? (content?.tokenInvalidSubtitle || "We couldn't process your invitation.")
    : (content?.accountRegisteredMessage || "Your company is already registered.");

  const guidance = isInvalid
    ? `${content?.tokenInvalidMessage || errorMessage} ${content?.tokenInvalidGuidance || "Please contact the company that sent the invitation for assistance."} ${content?.errorGuidance || "You can also try logging in if you already have an account."}`
    : `${content?.accountRegisteredSuffix || "Please login with your credentials to access your account. After logging in, you'll be able to view and respond to the invitation in your dashboard."}`;

  return (
    <div className="py-12">
      <WelcomeCard
        title={<span className={isInvalid ? "text-rose-500" : undefined}>{title}</span>}
        subtitle={description}
      />

      <motion.div
        {...INTRO}
        transition={{ ...spring, delay: 0.1 }}
        className="mx-auto max-w-3xl text-center mt-12"
      >
        <ButtonGroup className="justify-center! gap-5!">
          <Button
            variant="outline"
            onClick={() => window.location.href = HELP_URL}
          >
            {content?.buttonGetHelp || "Get help"}
          </Button>
          <Button onClick={() => window.location.href = '/'}>
            {content?.buttonLogin || "Login"}
          </Button>
        </ButtonGroup>
        <h4 className="mt-10 text-xs/4 text-zinc-400 font-medium">
          {guidance}
        </h4>
      </motion.div>
    </div>
  );
};

export default TokenStatusCard;
